import React from "react";
import { Box, Paper, Typography, Stack } from "@mui/material";
import { DASHBOARD_FONT } from "../manage/dashboardUi";

/** Same family as /manageRetreat. */
export const PROJECTED_FONT = DASHBOARD_FONT;

export const projectedPageFontSx = {
  fontFamily: PROJECTED_FONT,
  "& .MuiTypography-root, & .MuiButton-root, & .MuiInputBase-root, & .MuiInputLabel-root, & .MuiFormHelperText-root, & .MuiFormControlLabel-label, & .MuiChip-label, & .MuiAlert-message, & .MuiDialogTitle-root, & .MuiDialogContent-root, & .MuiMenuItem-root, & .MuiSelect-select, & .MuiTab-root":
    {
      fontFamily: PROJECTED_FONT,
    },
};

/** Azure mockup navy (sampled from Save Projected Retreat). */
export const PROJECTED_NAVY = "#1F5F8F";
export const PROJECTED_NAVY_HOVER = "#194E75";
export const PROJECTED_NAVY_TEXT = "#1E4167";

/** Sampled from the Azure mockup Setup Handoff panel. */
export const HANDOFF_PANEL_BG = "#F4F7FA";
export const HANDOFF_PANEL_BORDER = "#6BA8C1";
/** Sampled from the "Start Retreat Setup?" summary cards. */
export const HANDOFF_CARD_BG = "#FAFAFA";
export const HANDOFF_CARD_BORDER = "#E4E8ED";

export const projectedSaveButtonSx = {
  fontFamily: PROJECTED_FONT,
  textTransform: "none" as const,
  fontWeight: 700,
  color: "#ffffff",
  bgcolor: PROJECTED_NAVY,
  boxShadow: "none",
  "&:hover": {
    bgcolor: PROJECTED_NAVY_HOVER,
    boxShadow: "none",
  },
};

export const fieldSx = {
  fontFamily: PROJECTED_FONT,
  bgcolor: "#ffffff",
  "& .MuiInputBase-root, & .MuiInputLabel-root, & .MuiFormHelperText-root": {
    fontFamily: PROJECTED_FONT,
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    transition: "all 0.2s ease-in-out",
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#94a3b8",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#2563eb",
      borderWidth: 2,
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#2563eb",
    fontWeight: 600,
  },
};

export function SectionBlock({
  id,
  title,
  subtitle,
  icon,
  badge,
  children,
}: {
  id?: string;
  title: string;
  subtitle: string;
  icon?: React.ReactNode;
  badge?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Paper
      id={id}
      elevation={0}
      sx={{
        p: { xs: 2.5, sm: 3 },
        borderRadius: 3.5,
        bgcolor: "#ffffff",
        border: "1px solid #e2e8f0",
        scrollMarginTop: "11rem",
        boxShadow: "0 4px 20px -2px rgba(15, 23, 42, 0.04), 0 2px 6px -1px rgba(15, 23, 42, 0.02)",
        transition: "box-shadow 0.2s ease, border-color 0.2s ease",
        "&:hover": {
          boxShadow: "0 10px 25px -3px rgba(15, 23, 42, 0.07), 0 4px 10px -2px rgba(15, 23, 42, 0.03)",
          borderColor: "#cbd5e1",
        },
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1.5}
        sx={{ mb: 2.5 }}
      >
        <Stack direction="row" spacing={1.75} alignItems="center">
          {icon && (
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: 2.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
                color: "#1d4ed8",
                flexShrink: 0,
                boxShadow: "inset 0 1px 1px rgba(255, 255, 255, 0.8)",
              }}
            >
              {icon}
            </Box>
          )}
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: "#0f172a", fontSize: "1.1rem", letterSpacing: "-0.01em" }}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ color: "#64748b", fontSize: "0.85rem", mt: 0.25 }}>
              {subtitle}
            </Typography>
          </Box>
        </Stack>
        {badge && <Box sx={{ flexShrink: 0 }}>{badge}</Box>}
      </Stack>
      {children}
    </Paper>
  );
}

export function ReportSectionCard({
  title,
  accent = false,
  children,
}: {
  title: string;
  accent?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Box
      className="projected-report-card"
      sx={{
        border: "1px solid #d8e0e8",
        borderRadius: 1.25,
        overflow: "visible",
        bgcolor: "#fff",
        breakInside: "avoid",
        WebkitPrintColorAdjust: "exact",
        printColorAdjust: "exact",
      }}
    >
      <Typography
        className={
          accent
            ? "projected-report-card-title is-accent"
            : "projected-report-card-title"
        }
        sx={{
          px: 1.4,
          py: 0.75,
          fontSize: 11,
          fontWeight: 800,
          letterSpacing: ".08em",
          textTransform: "uppercase",
          color: accent ? "#fff" : "#14365d",
          bgcolor: accent ? "#14365d" : "#edf3f8",
          WebkitPrintColorAdjust: "exact",
          printColorAdjust: "exact",
        }}
      >
        {title}
      </Typography>
      <Box sx={{ p: 1.35 }}>{children}</Box>
    </Box>
  );
}

export function ReportDetailRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: ".06em",
          textTransform: "uppercase",
          color: "#5b6b7e",
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          mt: 0.2,
          fontSize: 12,
          lineHeight: 1.4,
          color: "#172b4d",
          overflowWrap: "break-word",
          wordBreak: "break-word",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

export function HandoffSummaryCard({
  label,
  title,
  children,
  tone = "muted",
}: {
  label: string;
  title: React.ReactNode;
  children?: React.ReactNode;
  /** `muted` matches the confirm-dialog cards; `plain` is white on the handoff panel. */
  tone?: "muted" | "plain";
}) {
  return (
    <Paper
      variant="outlined"
      elevation={0}
      sx={{
        p: 1.5,
        minWidth: 0,
        bgcolor: tone === "plain" ? "#ffffff" : HANDOFF_CARD_BG,
        borderColor: HANDOFF_CARD_BORDER,
        boxShadow: "none",
      }}
    >
      <Typography variant="caption" sx={{ fontWeight: 700, color: "#64748b" }}>
        {label}
      </Typography>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      {children ? (
        <Typography variant="body2" color="text.secondary">
          {children}
        </Typography>
      ) : null}
    </Paper>
  );
}

